/**
 * @file stepperCalendarRangeSync — sincronía duración en días (calendario) ↔ valor (stepper).
 *
 * ## Guía rápida de implementación
 *
 * **Contenedor (plugin):** un ancestro común del calendario y del stepper con `data-plugin="stepperCalendarRangeSync"`.
 * - `ComDateSelectorB2C` + `syncStepperRange`: el `<section>` del selector lleva el plugin (calendario y stepper en el slot footer).
 * - `ComModalB2B` + `syncStepperCalendarRange`: el panel del modal (`com-modalB2B__content`) lleva el plugin cuando el
 *   calendario va en `slot="content"` y el `CoreStepperV2` en `slot="footer"` (p. ej. `ComDateSelectorB2B` con `hasFooter={false}`).
 *
 * **Hijos obligatorios (dentro del mismo contenedor):**
 * - **Calendario:** un nodo con `data-plugin` que incluya `coreCalendar` (p. ej. el `CoreCalendar.astro`
 *   del proyecto), o la clase `.core-calendar-container`. Debe emitir `coreCalendar:change` (plugin CoreCalendar).
 * - **Stepper:** un `CoreStepperV2` con `data-plugin="stepper"` (recomendado) o, como mínimo,
 *   un elemento con clase `.coreStepperV2` para que coexista el plugin `stepper`.
 *
 * **Atributos del stepper (HTML / CoreStepperV2):**
 * - `data-min` / `data-max` / `data-value`: límites y valor inicial; el plugin ajusta `data-value`
 *   cuando cambia el rango en el calendario (sin disparar `stepperChange` en ese sentido).
 * - Los clics en +/- siguen disparando `stepperChange` con `{ value, oldValue, action }` como antes.
 *
 * **Calendario (Flatpickr):**
 * - Modo **`range`** recomendado si quieres que +/- cambien varios días manteniendo el inicio.
 * - Con una sola fecha seleccionada, la duración se interpreta como **1 día** hasta completar el rango.
 * - Tras `setDate` desde el stepper se restaura la vista de meses (`jumpToDate`) para no desplazar
 *   el bloque de `showMonths` (p. ej. 12 meses en vertical).
 *
 * Si falta el calendario o el stepper, el plugin registra un `console.warn` y no enlaza listeners.
 */
import type flatpickr from "flatpickr";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";
import { applyStepperValueToElement } from "./stepper";

const CALENDAR_CHANGE = "coreCalendar:change";

type CalendarChangeDetail = {
  selectedDates: Date[];
  dateStr: string;
  instance: flatpickr.Instance;
};

/** @see bloque @file arriba para la guía de implementación. */
export class StepperCalendarRangeSync implements Plugin {
  private calendarRoot: HTMLElement | null = null;
  private stepperRoot: HTMLElement | null = null;
  private fpInstance: flatpickr.Instance | undefined;
  private isApplyingFromStepper = false;

  private readonly onCalendarChangeBound = (event: Event): void => {
    this.onCalendarChange(event as CustomEvent<CalendarChangeDetail>);
  };

  private readonly onStepperChangeBound = (event: Event): void => {
    this.onStepperChange(event as CustomEvent<{ value: number }>);
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.calendarRoot =
      this.element.querySelector<HTMLElement>(
        '[data-plugin~="coreCalendar"]',
      ) ?? this.element.querySelector<HTMLElement>(".core-calendar-container");

    this.stepperRoot =
      this.element.querySelector<HTMLElement>(
        '.coreStepperV2[data-plugin~="stepper"]',
      ) ?? this.element.querySelector<HTMLElement>(".coreStepperV2");

    if (!this.calendarRoot || !this.stepperRoot) {
      console.warn(
        "stepperCalendarRangeSync: no se encontró coreCalendar y/o CoreStepperV2 dentro del contenedor.",
        this.element,
      );
      return;
    }

    this.calendarRoot.addEventListener(
      CALENDAR_CHANGE,
      this.onCalendarChangeBound,
    );
    this.stepperRoot.addEventListener(
      "stepperChange",
      this.onStepperChangeBound,
    );
  }

  destroy(): void {
    this.calendarRoot?.removeEventListener(
      CALENDAR_CHANGE,
      this.onCalendarChangeBound,
    );
    this.stepperRoot?.removeEventListener(
      "stepperChange",
      this.onStepperChangeBound,
    );
  }

  private onCalendarChange(event: CustomEvent<CalendarChangeDetail>): void {
    if (this.isApplyingFromStepper) {
      return;
    }

    const { selectedDates, instance } = event.detail;
    this.fpInstance = instance;

    const days = this.countInclusiveDays(selectedDates ?? []);
    if (days <= 0) {
      return;
    }

    applyStepperValueToElement(this.stepperRoot!, days);
  }

  private onStepperChange(event: CustomEvent<{ value: number }>): void {
    const value = event.detail?.value;
    if (value === undefined || !this.fpInstance) {
      return;
    }

    const selected = this.fpInstance.selectedDates;
    if (!selected.length) {
      return;
    }

    const start = new Date(selected[0]);
    start.setHours(0, 0, 0, 0);
    const end = this.endFromStartAndInclusiveDays(start, value);
    const mode = this.fpInstance.config.mode;

    const fp = this.fpInstance;
    const viewMonth = fp.currentMonth;
    const viewYear = fp.currentYear;

    this.isApplyingFromStepper = true;
    try {
      if (mode === "range") {
        fp.setDate([start, end], true);
      } else {
        fp.setDate(start, true);
      }
      // setDate mueve el mes visible al inicio del rango; mantenemos la vista que el usuario tenía (p. ej. 12 meses).
      fp.jumpToDate(new Date(viewYear, viewMonth, 1), false);
    } finally {
      this.isApplyingFromStepper = false;
    }
  }

  private countInclusiveDays(dates: Date[]): number {
    if (dates.length === 0) {
      return 0;
    }
    if (dates.length === 1) {
      return 1;
    }
    const start = dates[0].getTime();
    const end = dates[dates.length - 1].getTime();
    const diffMs = Math.abs(end - start);
    return Math.floor(diffMs / 86400000) + 1;
  }

  private endFromStartAndInclusiveDays(start: Date, days: number): Date {
    const safeDays = Math.max(1, days);
    const end = new Date(start);
    end.setDate(end.getDate() + safeDays - 1);
    return end;
  }
}

PluginRegistry.register("stepperCalendarRangeSync", StepperCalendarRangeSync);
